import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { errorMessage, fetchReportPdf, manualApply, scanClean, scanTicker, scanUpload } from "../lib/apiClient";
import { emptyFinancials, financialsFromInputTable } from "../lib/finlogic";
import { YEAR_LABELS } from "../lib/constants";
import { downloadBlob } from "../lib/format";
import { PeerRow, ScanData, ScanResult, Usage } from "../types";
import { useAuth } from "./AuthContext";

interface ScanContextValue {
  data: ScanData | null;
  scan: ScanResult | null;
  source: string;
  investorProfile: string;
  setInvestorProfile: (p: string) => void;
  peerRows: PeerRow[];
  setPeerRows: (rows: PeerRow[]) => void;
  // Peer Benchmark's full scan results, compare-target ticker, chosen filter
  // list and load failures live here (not as component-local state) because
  // Radix Tabs unmounts inactive panels by default - without this, switching
  // away from the Peer Benchmark tab and back would remount it from scratch
  // and silently wipe the head-to-head verdict, SWOT diff and filter table.
  peerScans: Record<string, ScanResult>;
  setPeerScans: (scans: Record<string, ScanResult>) => void;
  peerCompareTicker: string;
  setPeerCompareTicker: (ticker: string) => void;
  peerSelectedFilters: string[];
  setPeerSelectedFilters: (filters: string[]) => void;
  peerFailures: string[];
  setPeerFailures: (failures: string[]) => void;
  clearPeerComparison: () => void;
  // Which tabs the user has opened during this scan - an honest "reviewed"
  // proxy (not a "filled in") signal, used to drive the Overview tab's
  // research-progress checklist. Reset whenever a new scan is loaded.
  visitedTabs: Set<string>;
  markTabVisited: (tab: string) => void;
  loading: boolean;
  loadTicker: (ticker: string) => Promise<{ ok: boolean; message?: string }>;
  loadCsv: (file: File, ticker?: string) => Promise<{ ok: boolean; message?: string }>;
  startManual: (ticker: string) => void;
  applyManualEntries: (rows: Record<string, any>[], mode: string) => Promise<{ ok: boolean; message?: string }>;
  runSimulation: (simData: ScanData) => Promise<ScanResult>;
  scanPeer: (ticker: string) => Promise<ScanResult>;
  downloadPdf: () => Promise<{ ok: boolean; message?: string }>;
  clear: () => void;
}

// Exported (raw context + value type) only so the dev-only design preview
// harness (see pages/DesignPreviewPage.tsx) can mount the dashboard tabs
// against fixture data without a live API/session. Nothing in the app
// consumes these directly - useScan() below is still the only real entry.
export type { ScanContextValue };
export const ScanContext = createContext<ScanContextValue | undefined>(undefined);

function dataFromScan(scan: ScanResult, fallbackTicker: string): ScanData {
  const years = scan.years && scan.years.length ? scan.years : YEAR_LABELS;
  return {
    ticker: scan.ticker || fallbackTicker,
    years,
    financials: financialsFromInputTable(scan.input_table || [], years),
    market: { company_name: scan.company },
  };
}

export const ScanProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [data, setData] = useState<ScanData | null>(null);
  const [scan, setScan] = useState<ScanResult | null>(null);
  const [source, setSource] = useState("");
  const [investorProfile, setInvestorProfile] = useState("Quality Investor");
  const [peerRows, setPeerRows] = useState<PeerRow[]>([]);
  const [peerScans, setPeerScans] = useState<Record<string, ScanResult>>({});
  const [peerCompareTicker, setPeerCompareTicker] = useState("");
  const [peerSelectedFilters, setPeerSelectedFilters] = useState<string[]>([]);
  const [peerFailures, setPeerFailures] = useState<string[]>([]);
  const [visitedTabs, setVisitedTabs] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const { setUsage, refreshUsage, canUsePremiumPdf } = useAuth();

  // Guards against a slow/superseded request clobbering a newer one's result
  // (e.g. a user fires a scan, then quickly fires another before the first
  // resolves). Each load bumps this counter and captures its own value;
  // if a later load has since started by the time an earlier one resolves,
  // the earlier one's result is discarded instead of overwriting the
  // current, more recent scan.
  const requestEpoch = useRef(0);

  const absorbUsage = useCallback((result: ScanResult) => {
    if (result?.usage) setUsage(result.usage as Usage);
    else refreshUsage();
  }, [setUsage, refreshUsage]);

  const clearPeerComparison = useCallback(() => {
    setPeerRows([]);
    setPeerScans({});
    setPeerCompareTicker("");
    setPeerSelectedFilters([]);
    setPeerFailures([]);
  }, []);

  const markTabVisited = useCallback((tab: string) => {
    setVisitedTabs((prev) => (prev.has(tab) ? prev : new Set(prev).add(tab)));
  }, []);

  const loadTicker = useCallback(async (ticker: string) => {
    const myEpoch = ++requestEpoch.current;
    setLoading(true);
    try {
      const result = await scanTicker(ticker);
      if (myEpoch !== requestEpoch.current) return { ok: true };
      setScan(result);
      clearPeerComparison();
      setVisitedTabs(new Set());
      setData(dataFromScan(result, ticker));
      setSource(`Auto Fetch: ${ticker.toUpperCase()}`);
      absorbUsage(result);
      return { ok: true };
    } catch (err) {
      if (myEpoch !== requestEpoch.current) return { ok: true };
      return { ok: false, message: errorMessage(err, "Couldn't get usable numbers this time - even the exchange has an off day. Try again shortly.") };
    } finally {
      if (myEpoch === requestEpoch.current) setLoading(false);
    }
  }, [absorbUsage, clearPeerComparison]);

  const loadCsv = useCallback(async (file: File, ticker?: string) => {
    const myEpoch = ++requestEpoch.current;
    setLoading(true);
    try {
      const result = await scanUpload(file);
      if (ticker) result.ticker = ticker.toUpperCase();
      if (myEpoch !== requestEpoch.current) return { ok: true };
      setScan(result);
      clearPeerComparison();
      setVisitedTabs(new Set());
      setData(dataFromScan(result, ticker || "UPLOADED"));
      setSource(file.name);
      absorbUsage(result);
      return { ok: true };
    } catch (err) {
      if (myEpoch !== requestEpoch.current) return { ok: true };
      return { ok: false, message: errorMessage(err, "Couldn't read that file. A plain CSV export usually does the trick - try again.") };
    } finally {
      if (myEpoch === requestEpoch.current) setLoading(false);
    }
  }, [absorbUsage, clearPeerComparison]);

  const startManual = useCallback((ticker: string) => {
    const d: ScanData = {
      ticker: ticker.toUpperCase(),
      years: YEAR_LABELS,
      financials: emptyFinancials(),
      market: { company_name: ticker.toUpperCase() },
    };
    setData(d);
    setScan(null);
    clearPeerComparison();
    setVisitedTabs(new Set());
    setSource("Manual Entry");
  }, [clearPeerComparison]);

  const applyManualEntries = useCallback(async (rows: Record<string, any>[], mode: string) => {
    if (!data) return { ok: false, message: "Load or start a dataset first." };
    const myEpoch = ++requestEpoch.current;
    setLoading(true);
    try {
      const res = await manualApply(data, rows, mode, !scan);
      if (myEpoch !== requestEpoch.current) return { ok: true };
      setData(res.data);
      setScan(res.scan);
      clearPeerComparison();
      setVisitedTabs(new Set());
      absorbUsage(res.scan);
      return { ok: true };
    } catch (err) {
      if (myEpoch !== requestEpoch.current) return { ok: true };
      return { ok: false, message: errorMessage(err, "The scan data could not be calculated safely.") };
    } finally {
      if (myEpoch === requestEpoch.current) setLoading(false);
    }
  }, [data, scan, absorbUsage, clearPeerComparison]);

  const runSimulation = useCallback(async (simData: ScanData) => {
    return scanClean(simData, false, "simulation");
  }, []);

  const scanPeer = useCallback(async (ticker: string) => {
    return scanTicker(ticker, false, "peer_internal");
  }, []);

  const downloadPdf = useCallback(async () => {
    if (!data) return { ok: false, message: "No scan data available for the report." };
    try {
      const payload = canUsePremiumPdf && scan ? { ...scan, peer_comparison: peerRows } : data;
      const blob = await fetchReportPdf(payload, canUsePremiumPdf);
      const suffix = canUsePremiumPdf ? "pro_report" : "report";
      await downloadBlob(blob, `honey_scanner_${(data.ticker || "company").toLowerCase()}_${suffix}.pdf`);
      return { ok: true };
    } catch (err) {
      return { ok: false, message: errorMessage(err, "The report PDF could not be prepared. Please retry shortly.") };
    }
  }, [data, canUsePremiumPdf, peerRows, scan]);

  const clear = useCallback(() => {
    setData(null);
    setScan(null);
    clearPeerComparison();
    setVisitedTabs(new Set());
    setSource("");
  }, [clearPeerComparison]);

  const value = useMemo(
    () => ({
      data, scan, source, investorProfile, setInvestorProfile,
      peerRows, setPeerRows,
      peerScans, setPeerScans,
      peerCompareTicker, setPeerCompareTicker,
      peerSelectedFilters, setPeerSelectedFilters,
      peerFailures, setPeerFailures,
      clearPeerComparison,
      visitedTabs, markTabVisited,
      loading, loadTicker, loadCsv, startManual, applyManualEntries, runSimulation, scanPeer, downloadPdf, clear,
    }),
    [
      data, scan, source, investorProfile,
      peerRows, peerScans, peerCompareTicker, peerSelectedFilters, peerFailures, clearPeerComparison,
      visitedTabs, markTabVisited,
      loading, loadTicker, loadCsv, startManual, applyManualEntries, runSimulation, scanPeer, downloadPdf, clear,
    ]
  );

  return <ScanContext.Provider value={value}>{children}</ScanContext.Provider>;
};

export function useScan(): ScanContextValue {
  const ctx = useContext(ScanContext);
  if (!ctx) throw new Error("useScan must be used inside ScanProvider");
  return ctx;
}
